import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert, Button, Descriptions, Drawer } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { DRAWER_SIZE } from '@/const/drawer.ts'
import { AttachmentUpload } from '@/features/files/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { RENT_UNITS, RENTAL_STATUS } from './rental-list-search.ts'
import { ServiceOrderDetails } from './service-order-details.tsx'

dayjs.extend(utc)

type Source = { type: 'rental' | 'service'; orderId: number }

export function OrderDetailDrawer({ source, onClose }: { source: Source; onClose: () => void }) {
  const [open, setOpen] = useState(true)

  return (
    <Drawer
      open={open}
      size={DRAWER_SIZE}
      title={source.type === 'rental' ? '租赁订单详情' : '服务订单详情'}
      onClose={() => setOpen(false)}
      afterOpenChange={(visible) => {
        if (!visible) onClose()
      }}
    >
      {source.type === 'rental' ? <RentalDetails id={source.orderId} /> : <ServiceOrderDetails id={source.orderId} />}
    </Drawer>
  )
}

function RentalDetails({ id }: { id: number }) {
  const trpc = useTRPC()
  const query = useQuery(trpc.order.rental.detail.queryOptions({ rentalOrderId: id }))
  const order = query.data
  if (query.isError) {
    return (
      <Alert
        type="error"
        title={query.error.message}
        action={<Button onClick={() => void query.refetch()}>重试</Button>}
      />
    )
  }
  if (!order) {
    return <p>正在加载订单…</p>
  }
  return (
    <>
      <Descriptions
        column={{ xs: 1, sm: 2 }}
        items={[
          { key: 'no', label: '订单号', children: order.orderNo },
          { key: 'status', label: '订单状态', children: RENTAL_STATUS[order.status].text },
          { key: 'customer', label: '客户', children: order.customerName },
          { key: 'organization', label: '经营主体', children: order.organizationName },
          { key: 'business', label: '业务类型', children: order.businessName || '—' },
          { key: 'salesperson', label: '业务员', children: order.salespersonName || '—' },
          { key: 'subject', label: '承租主体', children: order.subjectName || order.contractPartyName || '—' },
          { key: 'site', label: '场地 / 房间', children: `${order.siteName} / ${order.roomNo || '—'}` },
          { key: 'start', label: '起租日期', children: dayjs.utc(order.startAt).format('YYYY-MM-DD') },
          { key: 'end', label: '结束日期', children: dayjs.utc(order.endAt).format('YYYY-MM-DD') },
          {
            key: 'price',
            label: '约定租金',
            children: `¥${formatAmount(order.agreedPrice)} / ${RENT_UNITS[order.rentType]}`
          },
          { key: 'interval', label: '收租周期', children: `每 ${order.billingInterval} ${RENT_UNITS[order.rentType]}` },
          {
            key: 'deposit',
            label: '押金',
            children: order.depositAmount === null ? '未约定' : `¥${formatAmount(order.depositAmount)}`
          },
          {
            key: 'management',
            label: '物业管理费',
            children: order.managementFee === null ? '—' : `¥${formatAmount(order.managementFee)}`
          },
          { key: 'note', label: '备注', children: order.note || '—', span: 2 }
        ]}
      />
      {order.attachments.length > 0 && (
        <AttachmentUpload value={order.attachments.map((file) => file.file.id)} disabled />
      )}
    </>
  )
}
